/**
 * A file's preview (Files, and an item's Video and Artwork): a side panel on a computer, the whole
 * screen on a phone (the kit's slide-in Modal). A picture is shown large and then cropped to each
 * shape the apps draw (2:3, 16:9, 1:1), around its focal point, so a cut-off face shows before it
 * reaches a TV. A video plays its own HLS (hls.js) with its length, its sizes and captions; one
 * still on its way says where it is instead of showing a broken player. "Used in" lists the items
 * that use the file (GET /admin/v1/media/assets/:id/uses); Delete waits until nothing does.
 */

import { useEffect, useState } from "react";
import Button from "@web-ui/components/Button";
import Modal from "@web-ui/components/Modal";
import Notice from "@web-ui/components/Notice";
import { media, type MediaAsset } from "@/api/admin";
import { useCatalogue } from "@/forms/form-context";
import { PickerCard } from "@/forms/controls/picker";
import { lengthWords } from "@/forms/time";
import { bytes, dateTime } from "@/lib/format";
import { useStudio } from "@/shell/studio";
import type { ImageRole } from "@media-domain/images";
import { explainMediaError } from "./errors";
import { ROLE_WORDS } from "./image-upload";
import { videoState } from "./states";
import { useUploads } from "./upload-center";
import { UploadStateLine } from "./upload-state";
import { useHls } from "./use-hls";

type VideoDetails = { durationSeconds?: number; width?: number; height?: number; master?: string; renditions?: Array<{ width: number; height: number; bandwidth: number }>; captions?: Array<{ locale: string; kind: string }>; poster?: string | null; ladder?: string };
type ImageDetails = { role?: string; sizes?: Array<{ width: number; height: number }>; focal?: { x: number; y: number }; color?: string };

/** A file inside a ready video's folder (its poster frame), from the master playlist's address. */
export function videoFileUrl(asset: MediaAsset, file: string | null | undefined): string | undefined {
  const master = (asset.details as VideoDetails | null)?.master;
  if (!file || !master || !asset.manifestUrl?.endsWith(master)) return undefined;
  return `${asset.manifestUrl.slice(0, -master.length)}${file}`;
}

/** The small picture Files shows for a file: an image's smallest size, a video's poster frame. */
export function fileThumb(asset: MediaAsset): string | undefined {
  if (asset.kind === "image") return (asset.feed as { sizes?: Array<{ url: string }> } | null)?.sizes?.[0]?.url;
  return videoFileUrl(asset, (asset.details as VideoDetails | null)?.poster);
}

export function FilePreview({ asset, onClose }: { asset: MediaAsset | null; onClose: () => void }) {
  return (
    <Modal open={!!asset} onClose={onClose} isSlidePanel closeLabel="Close the preview">
      {asset ? <PreviewBody key={asset.id} asset={asset} onClose={onClose} /> : null}
    </Modal>
  );
}

function PreviewBody({ asset, onClose }: { asset: MediaAsset; onClose: () => void }) {
  const { client, canEdit } = useStudio();
  const { stateOf, refresh } = useUploads();
  const catalogue = useCatalogue();
  const [uses, setUses] = useState<Array<{ type: string; id: string }> | null | "unknown">(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    client.call<{ uses: Array<{ type: string; id: string }> }>("GET", `/admin/v1/media/assets/${encodeURIComponent(asset.id)}/uses`)
      .then((r) => { if (live) setUses(r.uses); }).catch(() => { if (live) setUses("unknown"); });
    return () => { live = false; };
  }, [client, asset.id]);

  const remove = async () => {
    setBusy(true);
    try { await media.remove(client, asset.id); await refresh(); onClose(); }
    catch (e) { setError(explainMediaError(e)); } finally { setBusy(false); }
  };

  const used = Array.isArray(uses) ? uses.length : 0;
  return (
    <div className="flex h-full flex-col gap-5 overflow-y-auto p-6 pt-20 max-md:p-4 max-md:pt-18">
      <div>
        <h2 className="text-h5 break-words">{asset.title}</h2>
        <p className="text-body-2 text-t-secondary">{asset.kind === "video" ? "Video" : ROLE_WORDS[(asset.details as ImageDetails | null)?.role as ImageRole] ?? "Picture"} · added {dateTime(asset.createdAt)}{asset.storedBytes ? ` · ${bytes(asset.storedBytes)}` : ""}</p>
      </div>
      {asset.kind === "video" ? <VideoPreview asset={asset} state={stateOf(asset.id) ?? videoState(asset)} /> : <ImagePreview asset={asset} />}

      <section className="flex flex-col gap-2" aria-labelledby="used-in">
        <h3 id="used-in" className="text-sub-title-1">Used in</h3>
        {uses === null ? <p className="text-body-2 text-t-secondary">Looking…</p>
          : uses === "unknown" ? <p className="text-body-2 text-t-secondary">Where it is used could not be checked. Try again in a moment.</p>
          : used === 0 ? <p className="text-body-2 text-t-secondary">Not used anywhere. It is safe to delete.</p>
          : (
            <ul className="flex flex-col gap-2">
              {uses.map((u) => <li key={`${u.type}:${u.id}`}><PickerCard entity={catalogue?.find((e) => e.type === u.type && e.id === u.id)} missingId={u.id} /></li>)}
            </ul>
          )}
        {used >= 50 ? <p className="text-caption text-t-secondary">Showing the first 50.</p> : null}
      </section>

      {error ? <Notice tone="critical" onDismiss={() => setError("")}>{error}</Notice> : null}
      {canEdit && asset.status !== "uploading" ? (
        <div className="flex flex-col gap-1 border-t border-s-subtle pt-4">
          <div><Button type="button" variant="danger-soft" icon="trash" disabled={!Array.isArray(uses) || used > 0} isPending={busy} onClick={() => { if (window.confirm(`Delete “${asset.title}”? Its files are removed within the hour.`)) void remove(); }}>Delete</Button></div>
          {used > 0 ? <p className="text-caption text-t-secondary">{`Used by ${used} ${used === 1 ? "item" : "items"}; remove it from ${used === 1 ? "it" : "them"} first.`}</p> : null}
        </div>
      ) : null}
    </div>
  );
}

function VideoPreview({ asset, state }: { asset: MediaAsset; state: ReturnType<typeof videoState> }) {
  const d = (asset.details ?? {}) as VideoDetails;
  const ready = asset.status === "ready" && !!asset.manifestUrl;
  const { ref, error } = useHls(ready ? asset.manifestUrl : null);
  const poster = videoFileUrl(asset, d.poster);
  if (!ready) {
    return <div className="flex flex-col gap-3 rounded-2xl bg-b-surface2 p-4"><UploadStateLine state={state} /></div>;
  }
  const rungs = [...(d.renditions ?? [])].sort((a, b) => b.height - a.height).map((r) => `${r.height}p`);
  return (
    <div className="flex flex-col gap-3">
      <video ref={ref} controls playsInline poster={poster} className="aspect-video w-full rounded-2xl bg-b-dark1" aria-label={`Play ${asset.title}`} />
      {error ? <Notice tone="warning">{error}</Notice> : null}
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-body-2">
        <dt className="text-t-secondary">State</dt><dd>{state.label}</dd>
        <dt className="text-t-secondary">Length</dt><dd>{lengthWords(d.durationSeconds) || "Unknown"}</dd>
        <dt className="text-t-secondary">Sizes</dt><dd>{rungs.length ? rungs.join(", ") : "One size"}{d.width && d.height ? ` (largest ${d.width} × ${d.height})` : ""}</dd>
        <dt className="text-t-secondary">Captions</dt><dd>{d.captions?.length ? d.captions.map((c) => `${c.locale.toUpperCase()}${c.kind === "captions" ? " (with sounds)" : ""}`).join(", ") : "None"}</dd>
      </dl>
    </div>
  );
}

const CROPS: Array<{ label: string; aspect: string; className: string }> = [
  { label: "Poster, 2:3", aspect: "2 / 3", className: "w-[28%]" },
  { label: "Wide, 16:9", aspect: "16 / 9", className: "w-[44%]" },
  { label: "Square, 1:1", aspect: "1 / 1", className: "w-[24%]" },
];

function ImagePreview({ asset }: { asset: MediaAsset }) {
  const feed = (asset.feed ?? {}) as { url?: string; sizes?: Array<{ width: number; url: string }>; color?: string; focal?: { x: number; y: number } };
  const d = (asset.details ?? {}) as ImageDetails;
  const large = [...(feed.sizes ?? [])].sort((a, b) => a.width - b.width).find((s) => s.width >= 720)?.url ?? feed.url;
  const focal = feed.focal ?? d.focal ?? { x: 0.5, y: 0.5 };
  const position = `${Math.round(focal.x * 100)}% ${Math.round(focal.y * 100)}%`;
  const largest = d.sizes?.at(-1);
  if (!large) return <p className="text-body-2 text-t-secondary">This picture is not ready yet.</p>;
  return (
    <div className="flex flex-col gap-4">
      <img src={large} alt={asset.title} className="max-h-[50vh] w-full rounded-2xl bg-b-surface2 object-contain" style={{ backgroundColor: feed.color }} />
      {largest ? <p className="text-caption text-t-secondary">{`${largest.width} × ${largest.height} px · ${d.sizes!.length} sizes made for the apps`}</p> : null}
      <div className="flex flex-col gap-2">
        <h3 className="text-sub-title-2">How each shape crops it</h3>
        <p className="text-caption text-t-secondary">Where a row draws another shape, the apps cut it like this, around the point chosen at upload.</p>
        <div className="flex items-end gap-2">
          {CROPS.map((c) => (
            <figure key={c.label} className={`flex flex-col gap-1 ${c.className}`}>
              <img src={large} alt="" className="w-full rounded-xl object-cover" style={{ aspectRatio: c.aspect, objectPosition: position, backgroundColor: feed.color }} />
              <figcaption className="text-caption text-t-secondary">{c.label}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </div>
  );
}
